import { FeatureFlagsProvider } from '@ad-lucy/feature-flag-client-public';
import type { Gateway } from '@ad-lucy/gateway-provider';
import { GatewayProvider } from '@ad-lucy/gateway-provider';
import type { GatewayKeycloak } from '@ad-lucy/gateway-provider/keycloak';
import { ThemeProvider, ToastProvider } from '@ad-lucy/ui';
import { useI18n } from '@i18n/i18n.utils';
import { ErrorBoundary, LoaderCentered } from '@modules/components';
import {
  assertInjectedAppConfig,
  getAppConfig,
} from '@modules/config/appConfig';
import { LogoutFallback } from '@modules/Organizations';
import { AppRouter } from '@modules/router/AppRouter';
import { logViewportSize } from '@modules/services/datadog/logViewportSize';
import {
  bootstrapIntercom,
  useIntercomUpdateProfile,
} from '@modules/services/intercom';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
// @todo: Needs to be removed after adm refresh
import StylesReset from '../services/StylesReset';

// import styles from ui lib
import('@ad-lucy/ui/lib/assets/style.css');

const AppPreloader = () => {
  useIntercomUpdateProfile();

  return null;
};
// ThemeProvider will mount children only after the theme is loaded,
// only then we are able to display the UI lib loaders.
// To detect when the theme gets loaded, use this component as child of the ThemeProvider,
// which will hide the page loader served in the original html file.
function HidePageLoaderWhenMounted() {
  useLayoutEffect(() => {
    const pageLoader = globalThis.document.getElementById('page-loader');
    if (pageLoader) {
      pageLoader.style.display = 'none';
    }
  }, []);

  return null;
}
/**
 * main app component
 * init: Apollo, Custom Providers and Router
 */

type AppProps = {
  keycloak: GatewayKeycloak;
  gateway: Gateway;
};

export default function App({ gateway, keycloak }: Readonly<AppProps>) {
  const [showLogoutFallback, setShowLogoutFallback] = useState(false);
  const [loading, setLoading] = useState(true);
  const { i18n } = useI18n();

  useEffect(() => {
    async function initGatewayAuth() {
      try {
        // assert injected app config before initiating gateway provider and starting the app
        assertInjectedAppConfig();
        await gateway.init();

        if (gateway.user?.id) {
          logViewportSize(gateway.user?.id);

          const { INTERCOM_APP_ID } = getAppConfig();

          if (INTERCOM_APP_ID) {
            const { shutdownIntercom } = bootstrapIntercom({
              app_id: INTERCOM_APP_ID,
              // app_id: 'ghanad52', // intercom test instance app_id for reference
              user_hash: gateway.state.intercomHmac,
              // The gateway uses the user's email as user id to generate the user hash for Intercom
              user_id: gateway.user.email,
              email: gateway.user.email,
              language_override: i18n.resolvedLanguage,
            });
            keycloak.eventEmitter.on('onAuthRefreshError', shutdownIntercom);
            keycloak.eventEmitter.on('onAuthLogout', shutdownIntercom);
          }
        }
      } catch (error) {
        console.error(error);
        setShowLogoutFallback(true);
      } finally {
        setLoading(false);
      }
    }

    initGatewayAuth();
  }, [gateway, keycloak, i18n]);

  // app config has not been validated at this point, so we need to be defensive
  const themeConfig = useMemo(
    () => ({
      url: getAppConfig()?.THEME_URL,
      theme: getAppConfig()?.THEME_DEFAULT,
      namespace: 'adm-manager-v2',
    }),
    [],
  );

  // markup
  let content: JSX.Element | React.ReactNode = null;
  if (showLogoutFallback) {
    content = <LogoutFallback />;
  } else if (loading) {
    content = <LoaderCentered />;
  } else {
    content = (
      <>
        <AppPreloader />
        <AppRouter />
      </>
    );
  }

  return (
    <GatewayProvider
      gateway={gateway}
      clientName="adm-ui"
      clientVersion={import.meta.env.VITE_APP_VERSION ?? 'local'}
    >
      <FeatureFlagsProvider
        url={`${getAppConfig().API_ROOT}features/v1`}
        refetchIntervalInMs={getAppConfig().FF_REFETCH_INTERVAL}
      >
        <StylesReset />
        <ThemeProvider config={themeConfig}>
          <HidePageLoaderWhenMounted />
          <ToastProvider
            containerId="toastContainer"
            position="top-center"
            autoClose={2000}
            limit={undefined}
          />
          <ErrorBoundary fallback={<LogoutFallback />}>{content}</ErrorBoundary>
        </ThemeProvider>
      </FeatureFlagsProvider>
    </GatewayProvider>
  );
}
